import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  makeStyles,
  useTheme,
  useMediaQuery,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Select,
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  InputAdornment,
  Divider,
  Avatar,
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as ActiveIcon,
  Block as BlockedIcon,
  WarningRounded as WarningIcon,
  ErrorOutline as ExpiredIcon,
  Person as PersonIcon,
  Update as UpdateIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MoreHoriz as MoreHorizIcon,
  ExitToApp as EnterTenantIcon,
} from "@material-ui/icons";
import Menu from "@material-ui/core/Menu";
import { useHistory, useLocation } from "react-router-dom";

import { toast } from "react-toastify";
import { impersonateCompany } from "../../helpers/impersonate";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import ModalUsers from "../ModalUsers";
import api from "../../services/api";
import { head, isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import { i18n } from "../../translate/i18n";

import moment from "moment";

// ───── Mobile design tokens (per /tmp/mobile-design-system.md) ─────
// Verbatim from Admin/index.js (single source of truth).
const SP = { xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24, xxl: 32, hero: 48 };
const TT = { min: 44, primary: 48, hero: 56 };
const TYPE = {
  caption:    { fontSize: 12, lineHeight: 1.4, letterSpacing: 0.2 },
  body:       { fontSize: 14, lineHeight: 1.5 },
  bodyStrong: { fontSize: 15, lineHeight: 1.45, fontWeight: 500 },
  subheading: { fontSize: 17, lineHeight: 1.4,  fontWeight: 500 },
  heading:    { fontSize: 20, lineHeight: 1.3,  fontWeight: 600 },
};
const RAD = { pill: 999, sm: 8, md: 12, lg: 16, xl: 20 };
const COL = {
  brandGreen:     "#25d366",
  brandGreenDeep: "#128c7e",
  brandGreenSoft: "rgba(37,211,102,0.08)",
  textPrimary:    "#0B0D0C",
  textSecondary:  "#52525b",
  borderSoft:     "#eaeaea",
  pageBg:         "#f7faf8",
  cardBg:         "#ffffff",
  destructive:    "#ef4444",
  warning:        "#f59e0b",
};
// 16px input font defeats iOS Safari zoom-on-focus (spec A.4/B.7).
const MOBILE_INPUT_PROPS = { style: { fontSize: 16 } };

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  // CRM v3 design tokens — match Tags/Campaigns/Contacts.
  const accent     = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const hair       = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg         = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted    = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";

  return {
    root: {
      width: "100%",
      padding: 0,
      fontFamily: "Roboto, sans-serif",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 20,
      flexWrap: "wrap",
      gap: 12,
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    // Settings-section header pattern: brand-tinted icon-pill +
    // title/subtitle.
    headerIcon: {
      width: 38,
      height: 38,
      borderRadius: 11,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: accentSoft,
      "& .MuiSvgIcon-root": {
        fontSize: 20,
        color: accent,
      },
    },
    headerTitle: {
      fontFamily: "Roboto, sans-serif",
      fontSize: 18,
      fontWeight: 700,
      lineHeight: 1.2,
      color: fg,
      display: "flex",
      alignItems: "center",
      gap: 8,
      letterSpacing: "-0.005em",
    },
    headerSubtitle: {
      fontFamily: "Roboto, sans-serif",
      fontSize: 13,
      fontWeight: 400,
      color: fgMuted,
      marginTop: 2,
    },
    countChip: {
      fontFamily: "Roboto, sans-serif",
      fontWeight: 700,
      fontSize: 10.5,
      lineHeight: 1,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      backgroundColor: accentSoft,
      color: accent,
      borderRadius: 999,
      padding: "0 10px",
      height: 22,
      minWidth: 22,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    },
    addBtn: {
      background: COL.brandGreenDeep,
      color: "#fff",
      fontFamily: "Roboto, sans-serif",
      fontWeight: 600,
      fontSize: 14,
      padding: "9px 18px",
      borderRadius: 10,
      boxShadow: "0 4px 12px rgba(18,140,126,0.22)",
      textTransform: "none",
      "&:hover": {
        background: "#0d6e63",
        boxShadow: "0 6px 16px rgba(18,140,126,0.30)",
      },
    },
    // Search/filter bar pinned to top with subtle fill + hairline bottom.
    searchBar: {
      padding: "14px 16px",
      marginBottom: 16,
      borderRadius: 14,
      border: `1px solid ${hair}`,
      background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,22,20,0.015)",
      boxShadow: "none",
    },
    inputControl: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        background: isDark ? "#13181a" : "#ffffff",
        fontSize: 14,
        fontWeight: 500,
        fontFamily: "Roboto, sans-serif",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: hair,
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(15,22,20,0.12)",
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: accent,
        borderWidth: 1,
      },
    },
    resetBtn: {
      fontFamily: "Roboto, sans-serif",
      fontWeight: 500,
      fontSize: 13,
      textTransform: "none",
      borderRadius: 10,
      borderColor: hair,
      color: fgMuted,
      height: 40,
      "&:hover": {
        borderColor: accent,
        color: accent,
        background: accentSoft,
      },
    },
    // === DESKTOP TABLE — CRM v3 Paper ===
    mainPaper: {
      flex: 1,
      padding: 0,
      overflowY: "auto",
      borderRadius: 18,
      background: isDark ? "#13181a" : "#ffffff",
      border: `1px solid ${hair}`,
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 14px 36px rgba(0,0,0,0.32)"
        : "0 14px 36px rgba(15,22,20,0.06), 0 2px 6px rgba(15,22,20,0.04)",
      ...theme.scrollbarStyles,
      "& .MuiTable-root": {
        borderCollapse: "separate",
        borderSpacing: 0,
      },
      "& .MuiTableHead-root .MuiTableCell-head": {
        fontFamily: "Roboto, sans-serif",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: accent,
        background: isDark ? "rgba(46,227,122,0.04)" : "rgba(18,140,126,0.04)",
        borderBottom: `1px solid ${hair}`,
        padding: "16px 18px",
        whiteSpace: "nowrap",
      },
      "& .MuiTableBody-root .MuiTableCell-body": {
        fontFamily: "Roboto, sans-serif",
        fontSize: 14,
        fontWeight: 400,
        color: fg,
        borderBottom: `1px solid ${hair}`,
        padding: "18px 18px",
      },
      "& .MuiTableBody-root .MuiTableRow-root": {
        transition: "background 0.14s ease",
        "&:hover": {
          background: accentSoft,
          "& $rowActionsDesktop": { opacity: 1 },
        },
        "&:last-child .MuiTableCell-body": { borderBottom: "none" },
      },
      [theme.breakpoints.down("sm")]: { borderRadius: 14 },
    },
    tableHeadRow: {},
    tableHeadCell: {},
    tableRow: {
      position: "relative",
    },
    // Tenant name column: name (Roboto bold 16) + small meta (id, fgMuted 13).
    companyCell: {
      display: "flex",
      alignItems: "center",
      gap: 14,
    },
    companyAvatar: {
      width: 40,
      height: 40,
      fontSize: 14,
      fontWeight: 700,
      fontFamily: "Roboto, sans-serif",
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.25)",
    },
    companyName: {
      fontFamily: "Roboto, sans-serif",
      fontSize: 16,
      fontWeight: 700,
      lineHeight: 1.25,
      color: fg,
      letterSpacing: "-0.005em",
    },
    companyMeta: {
      fontFamily: "Roboto, sans-serif",
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 1.3,
      color: fgMuted,
      marginTop: 2,
    },
    // Plan badge — quieter, hairline-style chip.
    // maxWidth cap + label overflow guard so the chip never renders a
    // ".../n" or ".../D" fragment when the row is squeezed by the KPI
    // column on mobile. Mirrors the schedules-mobile-card fix.
    planChip: {
      fontFamily: "Roboto, sans-serif",
      fontWeight: 600,
      fontSize: 12,
      borderRadius: 999,
      letterSpacing: 0,
      padding: "0 2px",
      height: 24,
      maxWidth: "100%",
      // Linear-dense pill on xs: 2px 6px padding, 10px font, 4px radius,
      // no truncation
      [theme.breakpoints.down("xs")]: {
        fontSize: 10,
        height: "auto",
        borderRadius: 4,
        fontWeight: 500,
        letterSpacing: "0.01em",
        "& .MuiChip-label": {
          padding: "2px 6px",
          overflow: "visible",
          textOverflow: "clip",
          whiteSpace: "nowrap",
          lineHeight: "13px",
        },
      },
    },
    // Status pills: tinted hairline style.
    statusActive: {
      fontFamily: "Roboto, sans-serif",
      fontSize: 11.5,
      backgroundColor: accentSoft,
      color: accent,
      fontWeight: 700,
      border: `1px solid ${isDark ? "rgba(46,227,122,0.22)" : "rgba(18,140,126,0.18)"}`,
      borderRadius: 999,
      height: 24,
      letterSpacing: 0.2,
      maxWidth: "100%",
      // Linear-dense pill on xs: 2px 6px padding, 10px font, 4px radius
      [theme.breakpoints.down("xs")]: {
        fontSize: 10,
        height: "auto",
        letterSpacing: "0.01em",
        flexShrink: 0,
        borderRadius: 4,
        fontWeight: 500,
        backgroundColor: "rgba(37, 211, 102, 0.12)",
        color: "#0f7a3d",
        border: "1px solid rgba(37, 211, 102, 0.30)",
        "& .MuiChip-icon": { display: "none" },
        "& .MuiChip-label": {
          padding: "2px 6px",
          overflow: "visible",
          textOverflow: "clip",
          whiteSpace: "nowrap",
          lineHeight: "13px",
        },
      },
    },
    statusBlocked: {
      fontFamily: "Roboto, sans-serif",
      fontSize: 11.5,
      backgroundColor: isDark ? "rgba(239,68,68,0.10)" : "rgba(239,68,68,0.08)",
      color: isDark ? "#fca5a5" : "#b91c1c",
      fontWeight: 700,
      border: "1px solid rgba(239,68,68,0.22)",
      borderRadius: 999,
      height: 24,
      letterSpacing: 0.2,
      maxWidth: "100%",
      // Linear-dense pill on xs: 2px 6px padding, 10px font, 4px radius
      [theme.breakpoints.down("xs")]: {
        fontSize: 10,
        height: "auto",
        letterSpacing: "0.01em",
        flexShrink: 0,
        borderRadius: 4,
        fontWeight: 500,
        backgroundColor: "rgba(220, 38, 38, 0.08)",
        color: "#a01212",
        border: "1px solid rgba(220, 38, 38, 0.26)",
        "& .MuiChip-icon": { display: "none" },
        "& .MuiChip-label": {
          padding: "2px 6px",
          overflow: "visible",
          textOverflow: "clip",
          whiteSpace: "nowrap",
          lineHeight: "13px",
        },
      },
    },
    expirationOk: {
      fontFamily: "Roboto, sans-serif",
      color: fg,
      fontSize: 14,
      fontWeight: 500,
    },
    expirationWarn: {
      fontFamily: "Roboto, sans-serif",
      color: "#b45309",
      fontSize: 14,
      fontWeight: 600,
    },
    expirationDanger: {
      fontFamily: "Roboto, sans-serif",
      color: "#b91c1c",
      fontSize: 14,
      fontWeight: 600,
    },
    expirationRecurrence: {
      fontFamily: "Roboto, sans-serif",
      fontSize: 11,
      fontWeight: 600,
      color: fgMuted,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      marginTop: 1,
    },
    // Actions cell — collapsed to "..." menu on mobile, hover-revealed on desktop.
    actionsCell: {
      whiteSpace: "nowrap",
      width: 1,
    },
    rowActionsDesktop: {
      display: "inline-flex",
      gap: 4,
      opacity: 0.55,
      transition: "opacity 0.18s ease",
      "& .MuiIconButton-root": {
        color: fgMuted,
        padding: 8,
        transition: "color 0.14s, background 0.14s",
        "&:hover": {
          color: accent,
          background: accentSoft,
        },
        "&[data-variant='danger']:hover": {
          color: "#e25c5c",
          background: isDark ? "rgba(255,107,107,0.08)" : "rgba(220,38,38,0.06)",
        },
      },
    },
    // Desktop empty state — refined.
    emptyState: {
      padding: "56px 24px",
      textAlign: "center",
      color: fgMuted,
      fontFamily: "Roboto, sans-serif",
      "& .emptyHeadline": {
        fontFamily: "Roboto, sans-serif",
        fontSize: 16,
        fontWeight: 600,
        color: fg,
        marginTop: 12,
      },
      "& .emptySub": {
        fontFamily: "Roboto, sans-serif",
        fontSize: 13,
        fontWeight: 400,
        color: fgMuted,
        marginTop: 4,
      },
    },
    formGrid: {
      marginTop: theme.spacing(1),
    },
    fullWidth: { width: "100%" },

    // ============================================================
    // === MOBILE: SINGLE-COLUMN CARD STACK (CRM v3) ============
    // ============================================================
    // Hide the desktop Paper wrapper completely on mobile.
    desktopOnly: {
      [theme.breakpoints.down("xs")]: { display: "none" },
    },
    mobileOnly: {
      display: "none",
      [theme.breakpoints.down("xs")]: { display: "block" },
    },
    // === LINEAR-DENSE MOBILE CARD STACK (xs override) ===
    // Card stack wrapper — tighter vertical rhythm.
    mobileCatalog: {
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "4px 0 24px",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        minWidth: 0,
      },
    },
    // Linear-dense card: 12px padding, hairline border, no resting shadow,
    // status dot top-left instead of stripe.
    mobileCompanyCard: {
      [theme.breakpoints.down("xs")]: {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        background: isDark ? "#13181a" : "#ffffff",
        borderRadius: 8,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
        boxShadow: "none",
        overflow: "hidden",
        transition: "border-color 0.14s ease, box-shadow 0.14s ease",
        "&::before": {
          content: '""',
          position: "absolute",
          insetInlineStart: 12,
          top: 16,
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: "var(--stripe-color, #a3a3a3)",
        },
        "&:hover": {
          borderColor: isDark ? "rgba(255,255,255,0.14)" : "#d4d4d4",
          boxShadow: "0 1px 2px rgba(10,10,10,0.04)",
        },
      },
      "@media (max-width: 360px)": {
        borderRadius: 8,
      },
    },
    // Header row — avatar + name/meta + actions menu.
    mobileCompanyHeader: {
      [theme.breakpoints.down("xs")]: {
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr) auto",
        alignItems: "center",
        columnGap: 8,
        padding: "10px 12px 8px 26px",
      },
      "@media (max-width: 360px)": {
        padding: "10px 12px 6px 24px",
      },
    },
    mobileCompanyAvatar: {
      [theme.breakpoints.down("xs")]: {
        width: 28,
        height: 28,
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 500,
        fontFamily: "Roboto, sans-serif",
        boxShadow: "none",
        flexShrink: 0,
      },
    },
    mobileCompanyBody: {
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minWidth: 0,
        overflow: "visible",
      },
    },
    mobileCompanyName: {
      [theme.breakpoints.down("xs")]: {
        fontFamily: "Roboto, sans-serif",
        fontSize: 14,
        fontWeight: 500,
        lineHeight: "18px",
        color: isDark ? "rgba(255,255,255,0.92)" : "#18181b",
        // NEVER truncate — wrap with tight leading
        display: "block",
        overflow: "visible",
        textOverflow: "clip",
        whiteSpace: "normal",
        wordBreak: "normal",
        overflowWrap: "anywhere",
        letterSpacing: "-0.005em",
      },
      "@media (max-width: 360px)": {
        fontSize: 13,
        lineHeight: "17px",
      },
    },
    mobileCompanyMeta: {
      [theme.breakpoints.down("xs")]: {
        fontFamily: "Roboto, sans-serif",
        fontSize: 11,
        fontWeight: 400,
        letterSpacing: 0,
        textTransform: "none",
        color: isDark ? "rgba(255,255,255,0.45)" : "#71717a",
        lineHeight: "14px",
      },
      "@media (max-width: 360px)": {
        fontSize: 10,
        lineHeight: "13px",
      },
    },
    // "..." action trigger — icon glyph stays 16px, but hit target
    // expands to 44x44 on xs per WCAG / iOS HIG (Raviv 2026-06-05;
    // same pattern as pages/Connections tableIconBtn).
    mobileMoreBtn: {
      [theme.breakpoints.down("xs")]: {
        minWidth: TT.min,
        minHeight: TT.min,
        padding: 10,
        borderRadius: 4,
        color: isDark ? "rgba(255,255,255,0.55)" : "#a3a3a3",
        flexShrink: 0,
        "& .MuiSvgIcon-root": { fontSize: 16 },
        "&:hover": {
          color: isDark ? "rgba(255,255,255,0.92)" : "#0a0a0a",
          background: isDark ? "rgba(255,255,255,0.06)" : "#f4f4f5",
        },
      },
    },
    // Pinned summary row: status pill + plan + KPI inline (no wrap forcing).
    mobileCompanyPinned: {
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 6,
        padding: "0 12px 10px 26px",
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
      },
      "@media (max-width: 360px)": {
        padding: "0 12px 8px 24px",
      },
    },
    mobileCompanyKpi: {
      [theme.breakpoints.down("xs")]: {
        marginInlineStart: "auto",
        display: "flex",
        flexDirection: "row",
        alignItems: "baseline",
        gap: 4,
        flexShrink: 0,
      },
    },
    mobileKpiLabel: {
      [theme.breakpoints.down("xs")]: {
        fontFamily: "Roboto, sans-serif",
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: 0,
        textTransform: "none",
        color: isDark ? "rgba(255,255,255,0.45)" : "#71717a",
        lineHeight: "13px",
      },
    },
    mobileKpiValue: {
      [theme.breakpoints.down("xs")]: {
        fontFamily: "Roboto, sans-serif",
        fontSize: 12,
        fontWeight: 400,
        lineHeight: "16px",
        color: isDark ? "rgba(255,255,255,0.92)" : "#18181b",
        fontFeatureSettings: '"tnum" 1',
      },
    },
    // Tap-to-expand toggle — flat, sentence-case label per spec.
    // minHeight bumped 28→44 on xs so the row meets WCAG / iOS HIG
    // tap-target rule without affecting visible label/icon size
    // (Raviv 2026-06-05).
    mobileExpandBtn: {
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        minHeight: TT.min,
        padding: "6px 12px",
        borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#ededed"}`,
        background: "transparent",
        border: "none",
        borderTopWidth: 1,
        borderTopStyle: "solid",
        borderTopColor: isDark ? "rgba(255,255,255,0.06)" : "#ededed",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        fontFamily: "Roboto, sans-serif",
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: 0,
        textTransform: "none",
        color: isDark ? "rgba(255,255,255,0.55)" : "#71717a",
        transition: "color 0.14s, background 0.14s",
        "&:hover, &:focus": {
          color: isDark ? "rgba(255,255,255,0.92)" : "#0a0a0a",
          background: isDark ? "rgba(255,255,255,0.06)" : "#f4f4f5",
          outline: "none",
        },
        "& .MuiSvgIcon-root": {
          fontSize: 15,
        },
      },
    },
    // Expanded drill-down panel — KV list pattern.
    mobileCompanyExpand: {
      [theme.breakpoints.down("xs")]: {
        padding: "8px 12px 10px 26px",
        borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#ededed"}`,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        background: "transparent",
      },
      "@media (max-width: 360px)": {
        padding: "8px 12px 10px 24px",
      },
    },
    // KV pair: row layout with fixed-width label + flex value.
    mobileExpandRow: {
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        flexDirection: "row",
        alignItems: "baseline",
        gap: 6,
      },
    },
    mobileExpandLabel: {
      [theme.breakpoints.down("xs")]: {
        fontFamily: "Roboto, sans-serif",
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: 0,
        textTransform: "none",
        color: isDark ? "rgba(255,255,255,0.45)" : "#71717a",
        minWidth: 64,
        flexShrink: 0,
      },
      "@media (max-width: 360px)": {
        fontSize: 10,
        minWidth: 56,
      },
    },
    mobileExpandValue: {
      [theme.breakpoints.down("xs")]: {
        fontFamily: "Roboto, sans-serif",
        fontSize: 12,
        fontWeight: 400,
        lineHeight: "16px",
        color: isDark ? "rgba(255,255,255,0.92)" : "#18181b",
        overflowWrap: "anywhere",
        wordBreak: "normal",
      },
    },
    // Mobile empty state.
    mobileEmptyState: {
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        padding: "56px 24px 32px",
        textAlign: "center",
        "& .emptyNum": {
          fontFamily: "Roboto, sans-serif",
          fontSize: 56,
          fontWeight: 300,
          letterSpacing: "-0.02em",
          color: accent,
          lineHeight: 1,
          opacity: 0.85,
          margin: 0,
        },
        "& .emptyMeta": {
          fontFamily: "Roboto, sans-serif",
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: accent,
        },
        "& .emptyDesc": {
          fontFamily: "Roboto, sans-serif",
          fontSize: 14,
          lineHeight: 1.5,
          color: fgMuted,
          maxWidth: 260,
          margin: 0,
        },
      },
    },
    // Mobile actions menu (Material-UI Menu).
    mobileActionMenu: {
      "& .MuiPaper-root": {
        borderRadius: 14,
        border: `1px solid ${hair}`,
        background: isDark ? "#13181a" : "#ffffff",
        boxShadow: isDark
          ? "0 8px 24px rgba(0,0,0,0.4)"
          : "0 8px 24px rgba(15,22,20,0.10)",
        minWidth: 200,
        padding: 4,
      },
      "& .MuiMenuItem-root": {
        fontFamily: "Roboto, sans-serif",
        fontSize: 14,
        fontWeight: 500,
        minHeight: TT.min,
        padding: "10px 14px",
        borderRadius: 10,
        color: fg,
        gap: 12,
        "&:hover": {
          background: accentSoft,
          color: accent,
        },
        "&[data-variant='danger']": {
          color: isDark ? "#fca5a5" : "#b91c1c",
          "&:hover": {
            background: isDark ? "rgba(255,107,107,0.08)" : "rgba(220,38,38,0.06)",
            color: isDark ? "#fca5a5" : "#b91c1c",
          },
        },
        "& .MuiSvgIcon-root": { fontSize: 18 },
      },
    },
    // Header CTA: full-width on xs (B.2 primary CTA rule).
    addBtnMobile: {
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        minHeight: TT.primary,
      },
    },
    // Dialog actions: stack column-reverse on xs (destructive above primary).
    dialogActionsMobile: {
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column-reverse",
        padding: SP.md,
        gap: SP.xs,
        "& > *": {
          width: "100%",
          minHeight: TT.primary,
          marginInlineStart: "0 !important",
        },
        // The <Box flex={1} /> spacer collapses to zero on column.
        "& > .MuiBox-root": { display: "none" },
      },
    },
    "@keyframes companyCardIn": {
      "0%":   { opacity: 0, transform: "translateY(8px)" },
      "100%": { opacity: 1, transform: "translateY(0)" },
    },
  };
});

// Plan badge color — was a random rainbow tied to plan name (purple
// / cyan / orange). Now a brand-friendly 5-tone scale so plan badges
// sit alongside the rest of the green Settings page without screaming.
const planColor = (planName = "") => {
  const lower = planName.toLowerCase();
  if (lower.includes("enterprise")) return "#075e54";
  if (lower.includes("pro")) return "#128c7e";
  if (lower.includes("test")) return "#64748b";
  if (lower.includes("trial")) return "#22c55e";
  return "#25d366";
};

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("") || "?";

// Brand-tinted 8-shade avatar palette (same one used in
// helpers/colorGenerator.js). Replaces purple / cyan / pink mix
// the table previously generated.
const avatarColors = [
  "#075e54",
  "#0a7164",
  "#0d8474",
  "#128c7e",
  "#16a34a",
  "#1ebe5d",
  "#22c55e",
  "#25d366",
];

const colorFromName = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash << 5) - hash + name.charCodeAt(i);
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

function CompanyDialog({ open, onClose, initialValue, onSubmit, onDelete, loading }) {
  const classes = useStyles();
  const theme = useTheme();
  // fullScreen on xs (<600px) so the 6-field edit form has room to breathe;
  // the centered maxWidth='sm' modal was clipped on both sides on 360px.
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});
  const isEdit = Boolean(initialValue?.id);

  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    dueDate: "",
    recurrence: "",
    ...initialValue,
  });

  const { list: listPlans } = usePlans();

  useEffect(() => {
    let mounted = true;
    listPlans().then((list) => mounted && setPlans(list || []));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    setRecord((prev) => {
      const next = { ...prev, ...(initialValue || {}) };
      if (next.dueDate && moment(next.dueDate).isValid()) {
        next.dueDate = moment(next.dueDate).format("YYYY-MM-DD");
      }
      return next;
    });
  }, [initialValue, open]);

  const handleSubmit = async (data) => {
    if (data.dueDate === "" || moment(data.dueDate).isValid() === false) {
      data.dueDate = null;
    }
    await onSubmit(data);
  };

  const handleOpenModalUsers = async () => {
    try {
      const { data } = await api.get("/users/list", {
        params: { companyId: initialValue.id },
      });
      if (isArray(data) && data.length) {
        setFirstUser(head(data));
      }
      setModalUser(true);
    } catch (e) {
      toast.error(e);
    }
  };

  const incrementDueDate = (currentValues, setFieldValue) => {
    if (!currentValues.dueDate || !currentValues.recurrence) return;
    const map = {
      MENSAL: 1,
      BIMESTRAL: 2,
      TRIMESTRAL: 3,
      SEMESTRAL: 6,
      ANUAL: 12,
    };
    const months = map[currentValues.recurrence];
    if (!months) return;
    const next = moment(currentValues.dueDate).add(months, "month").format("YYYY-MM-DD");
    setFieldValue("dueDate", next);
  };

  return (
    <>
      <ModalUsers
        userId={firstUser.id}
        companyId={initialValue?.id}
        open={modalUser}
        onClose={() => {
          setFirstUser({});
          setModalUser(false);
        }}
      />
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={12}>
            <Avatar className={classes.headerIcon} style={{ width: 36, height: 36 }}>
              <BusinessIcon />
            </Avatar>
            <Box ml={1.5}>
              <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1.2 }}>
                {isEdit ? "Edit Company" : "Add Company"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {isEdit
                  ? `Update details for ${initialValue?.name || "this company"}`
                  : "Create a new tenant on the platform"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Formik
          enableReinitialize
          initialValues={record}
          onSubmit={(values, { resetForm }) => {
            setTimeout(async () => {
              await handleSubmit(values);
              resetForm();
            }, 200);
          }}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Grid spacing={2} container className={classes.formGrid}>
                  <Grid xs={12} sm={6} item>
                    <Field
                      as={TextField}
                      label={i18n.t("companiesManager.form.name")}
                      name="name"
                      variant="outlined"
                      className={classes.fullWidth}
                      size="small"
                      inputProps={MOBILE_INPUT_PROPS}
                    />
                  </Grid>
                  <Grid xs={12} sm={6} item>
                    <Field
                      as={TextField}
                      label={i18n.t("companiesManager.form.email")}
                      name="email"
                      variant="outlined"
                      className={classes.fullWidth}
                      size="small"
                      required
                      inputProps={MOBILE_INPUT_PROPS}
                    />
                  </Grid>
                  <Grid xs={12} sm={6} item>
                    <Field
                      as={TextField}
                      label={i18n.t("companiesManager.form.phone")}
                      name="phone"
                      variant="outlined"
                      className={classes.fullWidth}
                      size="small"
                      inputProps={MOBILE_INPUT_PROPS}
                    />
                  </Grid>
                  <Grid xs={12} sm={6} item>
                    <FormControl variant="outlined" fullWidth size="small">
                      <InputLabel>{i18n.t("companiesManager.form.plan")}</InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("companiesManager.form.plan")}
                        name="planId"
                        required
                      >
                        {plans.map((plan, key) => (
                          <MenuItem key={key} value={plan.id}>
                            {plan.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={6} item>
                    <FormControl variant="outlined" fullWidth size="small">
                      <InputLabel>{i18n.t("companiesManager.form.status")}</InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("companiesManager.form.status")}
                        name="status"
                      >
                        <MenuItem value={true}>Active</MenuItem>
                        <MenuItem value={false}>Blocked</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={6} item>
                    <Field
                      as={TextField}
                      label={i18n.t("companiesManager.form.expiration")}
                      type="date"
                      name="dueDate"
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      className={classes.fullWidth}
                      size="small"
                      inputProps={MOBILE_INPUT_PROPS}
                    />
                  </Grid>
                  {isEdit && (
                    <Grid xs={12} item>
                      <Box display="flex" gap={8} flexWrap="wrap">
                        <Button
                          variant="outlined"
                          startIcon={<UpdateIcon />}
                          onClick={() => incrementDueDate(values, setFieldValue)}
                          disabled={!values.dueDate || !values.recurrence}
                        >
                          Extend by recurrence
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<PersonIcon />}
                          onClick={handleOpenModalUsers}
                        >
                          Manage admin user
                        </Button>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions style={{ padding: 16 }} className={classes.dialogActionsMobile}>
                {isEdit && (
                  <Button
                    onClick={() => onDelete(initialValue)}
                    color="secondary"
                    startIcon={<DeleteIcon />}
                  >
                    Delete
                  </Button>
                )}
                <Box flex={1} />
                <Button onClick={onClose}>Cancel</Button>
                <ButtonWithSpinner
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={loading}
                >
                  {isEdit ? "Save changes" : "Create company"}
                </ButtonWithSpinner>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
}

function ExpirationCell({ dueDate, recurrence, classes, dateToClient }) {
  if (!dueDate || !moment(dueDate).isValid()) {
    return <Typography variant="body2" color="textSecondary">—</Typography>;
  }
  const now = moment();
  const due = moment(dueDate);
  const diff = due.diff(now, "days");

  let cls = classes.expirationOk;
  let Icon = CalendarIcon;
  let iconColor = "inherit";
  if (diff < 0) {
    cls = classes.expirationDanger;
    Icon = ExpiredIcon;
    iconColor = "#b91c1c";
  } else if (diff <= 7) {
    cls = classes.expirationWarn;
    Icon = WarningIcon;
    iconColor = "#b45309";
  }

  return (
    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
      <Icon style={{ fontSize: 16, color: iconColor }} />
      <Box>
        <Typography component="div" className={cls}>
          {dateToClient(dueDate)}
        </Typography>
        {recurrence && (
          <Typography component="div" className={classes.expirationRecurrence}>
            {recurrence}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// Map a `?status=` URL token to the internal filter mode used in `filters.status`.
// Lets the Overview dashboard's "Suspended (3)" pill deep-link straight into
// CompaniesManager pre-filtered. Tokens that don't map fall back to "all".
const URL_STATUS_TO_FILTER = {
  suspended: "blocked",
  blocked: "blocked",
  active: "active",
  past_due: "past_due",
  expiring: "expiring",
  all: "all",
};

const enterTenant = (row) => {
  if (!row) return;
  if (
    !window.confirm(
      `Enter ${row.name || "this tenant"} as their admin? Your admin session ` +
        `is saved — use the banner at the top to return.`
    )
  )
    return;
  impersonateCompany(row.id).catch((e) =>
    toast.error(
      (e && e.response && e.response.data && e.response.data.error) ||
        (e && e.message) ||
        "Impersonation failed"
    )
  );
};

export default function CompaniesManager() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { dateToClient } = useDate();
  const { list, save, update, remove } = useCompanies();
  const { list: listPlans } = usePlans();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [plans, setPlans] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  // Mobile card UX: per-card expand toggle and "..." action menu.
  const [expandedId, setExpandedId] = useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [mobileMenuRow, setMobileMenuRow] = useState(null);

  const toggleExpand = useCallback((id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const openMobileMenu = useCallback((e, row) => {
    setMobileMenuAnchor(e.currentTarget);
    setMobileMenuRow(row);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuAnchor(null);
    setMobileMenuRow(null);
  }, []);

  // Init filter from URL `?status=...` so the Overview "Suspended/Past-due"
  // pills can deep-link in with a filter pre-applied. Falls back to "all".
  const initialStatus = useMemo(() => {
    const search = new URLSearchParams(location.search || "");
    const raw = (search.get("status") || "").toLowerCase();
    return URL_STATUS_TO_FILTER[raw] || "all";
  }, [location.search]);

  const [filters, setFilters] = useState({
    search: "",
    status: initialStatus,
    planId: "all",
  });

  // Re-sync if the URL changes after mount (back/forward, another deep-link).
  useEffect(() => {
    setFilters((prev) => {
      if (prev.status === initialStatus) return prev;
      return { ...prev, status: initialStatus };
    });
  }, [initialStatus]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [companyList, plansList] = await Promise.all([list(), listPlans()]);
      setRecords(companyList || []);
      setPlans(plansList || []);
    } catch (e) {
      toast.error("Could not load companies");
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadData();
      setDialogOpen(false);
      setEditingRecord(null);
      toast.success("Saved successfully");
    } catch (e) {
      toast.error(
        "Save failed. Check that the email is unique and all required fields are filled."
      );
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setLoading(true);
    try {
      await remove(pendingDelete.id);
      await loadData();
      setShowConfirmDialog(false);
      setPendingDelete(null);
      setDialogOpen(false);
      setEditingRecord(null);
      toast.success("Deleted successfully");
    } catch (e) {
      toast.error("Delete failed");
    }
    setLoading(false);
  };

  const openCreate = () => {
    setEditingRecord({
      name: "",
      email: "",
      phone: "",
      planId: "",
      status: true,
      dueDate: "",
      recurrence: "",
    });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingRecord({
      id: row.id,
      name: row.name || "",
      email: row.email || "",
      phone: row.phone || "",
      planId: row.planId || "",
      status: row.status === false ? false : true,
      dueDate: row.dueDate || "",
      recurrence: row.recurrence || "",
    });
    setDialogOpen(true);
  };

  const filtered = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const now = moment();
    return records.filter((r) => {
      if (filters.status === "active" && r.status === false) return false;
      if (filters.status === "blocked" && r.status !== false) return false;
      if (filters.status === "past_due") {
        if (!r.dueDate) return false;
        const due = moment(r.dueDate);
        if (!due.isValid()) return false;
        if (due.toDate() >= now.toDate()) return false;
      }
      if (filters.status === "expiring") {
        if (!r.dueDate) return false;
        const due = moment(r.dueDate);
        if (!due.isValid()) return false;
        const days = due.diff(now, "days");
        if (days < 0 || days > 7) return false;
      }
      if (filters.planId !== "all" && String(r.planId) !== String(filters.planId))
        return false;
      if (search) {
        const blob = `${r.name || ""} ${r.email || ""} ${r.phone || ""}`.toLowerCase();
        if (!blob.includes(search)) return false;
      }
      return true;
    });
  }, [records, filters]);

  const stats = useMemo(() => {
    const total = records.length;
    const active = records.filter((r) => r.status !== false).length;
    const expiringSoon = records.filter((r) => {
      if (!r.dueDate || !moment(r.dueDate).isValid()) return false;
      const diff = moment(r.dueDate).diff(moment(), "days");
      return diff >= 0 && diff <= 7;
    }).length;
    return { total, active, expiringSoon };
  }, [records]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <span className={classes.headerIcon}>
            <BusinessIcon />
          </span>
          <Box>
            <Typography variant="h5" className={classes.headerTitle} component="div">
              {i18n.t("settings.Companies.title", { defaultValue: "Companies" })}
              <span className={classes.countChip}>{stats.total}</span>
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {i18n.t("settings.companies.subtitle", {
                active: stats.active,
                expiring: stats.expiringSoon,
                defaultValue: `${stats.active} active · ${stats.expiringSoon} expiring within 7 days`,
              })}
            </Typography>
          </Box>
        </Box>
        <Button
          className={`${classes.addBtn} ${classes.addBtnMobile}`}
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          {i18n.t("settings.companies.add", { defaultValue: "Add Company" })}
        </Button>
      </Box>

      <Paper className={classes.searchBar} elevation={0}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              className={classes.inputControl}
              placeholder={i18n.t("settings.companies.searchPlaceholder", {
                defaultValue: "Search by name, email or phone...",
              })}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small" variant="outlined" className={classes.inputControl}>
              <InputLabel>{i18n.t("settings.companies.statusLabel", { defaultValue: "Status" })}</InputLabel>
              <Select
                label={i18n.t("settings.companies.statusLabel", { defaultValue: "Status" })}
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="all">{i18n.t("settings.companies.allStatuses", { defaultValue: "All statuses" })}</MenuItem>
                <MenuItem value="active">{i18n.t("settings.companies.activeOnly", { defaultValue: "Active only" })}</MenuItem>
                <MenuItem value="blocked">{i18n.t("settings.companies.blockedOnly", { defaultValue: "Blocked only" })}</MenuItem>
                <MenuItem value="expiring">{i18n.t("settings.companies.expiringOnly", { defaultValue: "Expiring within 7 days" })}</MenuItem>
                <MenuItem value="past_due">{i18n.t("settings.companies.pastDueOnly", { defaultValue: "Past due" })}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small" variant="outlined" className={classes.inputControl}>
              <InputLabel>{i18n.t("settings.companies.planLabel", { defaultValue: "Plan" })}</InputLabel>
              <Select
                label={i18n.t("settings.companies.planLabel", { defaultValue: "Plan" })}
                value={filters.planId}
                onChange={(e) => setFilters({ ...filters, planId: e.target.value })}
              >
                <MenuItem value="all">{i18n.t("settings.companies.allPlans", { defaultValue: "All plans" })}</MenuItem>
                {plans.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              fullWidth
              variant="outlined"
              className={classes.resetBtn}
              onClick={() => setFilters({ search: "", status: "all", planId: "all" })}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ===== DESKTOP TABLE ===== */}
      <Paper className={`${classes.mainPaper} ${classes.desktopOnly}`} elevation={0}>
        <div style={{ overflowX: "auto", width: "100%" }}>
        <Table size="small">
          <TableHead>
            <TableRow className={classes.tableHeadRow}>
              <TableCell className={classes.tableHeadCell}>
                {i18n.t("settings.companies.col.company", { defaultValue: "Company" })}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {i18n.t("settings.companies.col.contact", { defaultValue: "Contact" })}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {i18n.t("settings.companies.col.plan", { defaultValue: "Plan" })}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {i18n.t("settings.companies.col.status", { defaultValue: "Status" })}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {i18n.t("settings.companies.col.expiration", { defaultValue: "Expiration" })}
              </TableCell>
              <TableCell className={classes.tableHeadCell} align="right">
                {i18n.t("settings.companies.col.actions", { defaultValue: "Actions" })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className={classes.emptyState}>
                  <BusinessIcon style={{ fontSize: 44, opacity: 0.3 }} />
                  <Typography className="emptyHeadline">
                    No companies match your filters
                  </Typography>
                  <Typography className="emptySub">
                    Try adjusting filters or click "Add Company" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((row) => {
                const isActive = row.status !== false;
                const planName = row.plan?.name || "—";
                return (
                  <TableRow key={row.id} className={classes.tableRow}>
                    <TableCell data-label={i18n.t("settings.companies.col.company", { defaultValue: "Company" })}>
                      <Box className={classes.companyCell}>
                        <Avatar
                          className={classes.companyAvatar}
                          style={{ backgroundColor: colorFromName(row.name || "?") }}
                        >
                          {initials(row.name)}
                        </Avatar>
                        <Box>
                          <Typography className={classes.companyName} component="div">
                            {row.name || "—"}
                          </Typography>
                          <Typography className={classes.companyMeta} component="div">
                            ID #{row.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell data-label={i18n.t("settings.companies.col.contact", { defaultValue: "Contact" })}>
                      <Typography className={classes.companyName} component="div" style={{ fontSize: 14, fontWeight: 500 }}>
                        {row.email || "—"}
                      </Typography>
                      <Typography className={classes.companyMeta} component="div">
                        {row.phone || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell data-label={i18n.t("settings.companies.col.plan", { defaultValue: "Plan" })}>
                      {row.plan ? (
                        <Chip
                          label={planName}
                          size="small"
                          className={classes.planChip}
                          style={{
                            backgroundColor: `${planColor(planName)}1a`,
                            color: planColor(planName),
                            border: `1px solid ${planColor(planName)}33`,
                          }}
                        />
                      ) : (
                        <Typography className={classes.companyMeta} component="div">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell data-label={i18n.t("settings.companies.col.status", { defaultValue: "Status" })}>
                      <Chip
                        size="small"
                        icon={
                          isActive ? (
                            <ActiveIcon style={{ color: "#128c7e", fontSize: 14 }} />
                          ) : (
                            <BlockedIcon style={{ color: "#dc2626", fontSize: 14 }} />
                          )
                        }
                        label={isActive
                          ? i18n.t("settings.companies.statusActive", { defaultValue: "Active" })
                          : i18n.t("settings.companies.statusBlocked", { defaultValue: "Blocked" })}
                        className={
                          isActive ? classes.statusActive : classes.statusBlocked
                        }
                      />
                    </TableCell>
                    <TableCell data-label={i18n.t("settings.companies.col.expiration", { defaultValue: "Expiration" })}>
                      <ExpirationCell
                        dueDate={row.dueDate}
                        recurrence={row.recurrence}
                        classes={classes}
                        dateToClient={dateToClient}
                      />
                    </TableCell>
                    <TableCell data-label="" align="right" className={classes.actionsCell}>
                      <span className={classes.rowActionsDesktop}>
                        <Tooltip title={i18n.t("settings.companies.action.enterTenant", { defaultValue: "Enter tenant" })}>
                          <IconButton onClick={() => enterTenant(row)} size="small">
                            <EnterTenantIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={i18n.t("settings.companies.action.viewHealth", { defaultValue: "View health" })}>
                          <IconButton
                            onClick={() => history.push(`/admin/companies/${row.id}`)}
                            size="small"
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton onClick={() => openEdit(row)} size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            data-variant="danger"
                            onClick={() => {
                              setPendingDelete(row);
                              setShowConfirmDialog(true);
                            }}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        </div>
      </Paper>

      {/* ===== MOBILE CARD STACK ===== */}
      <div className={classes.mobileOnly}>
        {filtered.length === 0 ? (
          <div className={classes.mobileEmptyState}>
            <div className="emptyNum">0</div>
            <div className="emptyMeta">NO COMPANIES</div>
            <div className="emptyDesc">
              Try adjusting filters or tap "Add Company" to create one.
            </div>
          </div>
        ) : (
          <div className={classes.mobileCatalog}>
            {filtered.map((row, i) => {
              const isActive = row.status !== false;
              const planName = row.plan?.name || "—";
              const stripeColor = isActive ? "#25d366" : "rgba(239,68,68,0.7)";
              const expanded = expandedId === row.id;
              return (
                <div
                  key={row.id}
                  className={classes.mobileCompanyCard}
                  style={{
                    "--i": i,
                    "--stripe-color": stripeColor,
                    "--stripe-w": "3px",
                  }}
                >
                  <div className={classes.mobileCompanyHeader}>
                    <Avatar
                      className={classes.mobileCompanyAvatar}
                      style={{ backgroundColor: colorFromName(row.name || "?") }}
                    >
                      {initials(row.name)}
                    </Avatar>
                    <div className={classes.mobileCompanyBody}>
                      <div className={classes.mobileCompanyName}>
                        {row.name || "—"}
                      </div>
                      <div className={classes.mobileCompanyMeta}>
                        ID #{row.id}
                      </div>
                    </div>
                    <IconButton
                      className={classes.mobileMoreBtn}
                      onClick={(e) => openMobileMenu(e, row)}
                      aria-label="Actions"
                    >
                      <MoreHorizIcon />
                    </IconButton>
                  </div>

                  <div className={classes.mobileCompanyPinned}>
                    <Chip
                      size="small"
                      label={isActive
                        ? i18n.t("settings.companies.statusActive", { defaultValue: "Active" })
                        : i18n.t("settings.companies.statusBlocked", { defaultValue: "Blocked" })}
                      className={isActive ? classes.statusActive : classes.statusBlocked}
                    />
                    {row.plan && (
                      <Chip
                        size="small"
                        label={planName}
                        className={classes.planChip}
                        style={{
                          backgroundColor: `${planColor(planName)}1a`,
                          color: planColor(planName),
                          border: `1px solid ${planColor(planName)}33`,
                        }}
                      />
                    )}
                    <div className={classes.mobileCompanyKpi}>
                      <div className={classes.mobileKpiLabel}>Created</div>
                      <div className={classes.mobileKpiValue}>
                        {row.createdAt ? dateToClient(row.createdAt) : "—"}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className={classes.mobileExpandBtn}
                    onClick={() => toggleExpand(row.id)}
                    aria-expanded={expanded}
                  >
                    <span>{expanded ? "Hide details" : "Details"}</span>
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </button>

                  {expanded && (
                    <div className={classes.mobileCompanyExpand}>
                      <div className={classes.mobileExpandRow}>
                        <div className={classes.mobileExpandLabel}>Email</div>
                        <div className={classes.mobileExpandValue}>{row.email || "—"}</div>
                      </div>
                      <div className={classes.mobileExpandRow}>
                        <div className={classes.mobileExpandLabel}>Phone</div>
                        <div className={classes.mobileExpandValue}>{row.phone || "—"}</div>
                      </div>
                      <div className={classes.mobileExpandRow}>
                        <div className={classes.mobileExpandLabel}>Expiration</div>
                        <div className={classes.mobileExpandValue}>
                          <ExpirationCell
                            dueDate={row.dueDate}
                            recurrence={row.recurrence}
                            classes={classes}
                            dateToClient={dateToClient}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <Menu
          className={classes.mobileActionMenu}
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={closeMobileMenu}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              if (mobileMenuRow) enterTenant(mobileMenuRow);
              closeMobileMenu();
            }}
          >
            <EnterTenantIcon /> {i18n.t("settings.companies.action.enterTenant", { defaultValue: "Enter tenant" })}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (mobileMenuRow) history.push(`/admin/companies/${mobileMenuRow.id}`);
              closeMobileMenu();
            }}
          >
            <ViewIcon /> {i18n.t("settings.companies.action.viewHealth", { defaultValue: "View health" })}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (mobileMenuRow) openEdit(mobileMenuRow);
              closeMobileMenu();
            }}
          >
            <EditIcon /> Edit
          </MenuItem>
          <MenuItem
            data-variant="danger"
            onClick={() => {
              if (mobileMenuRow) {
                setPendingDelete(mobileMenuRow);
                setShowConfirmDialog(true);
              }
              closeMobileMenu();
            }}
          >
            <DeleteIcon /> Delete
          </MenuItem>
        </Menu>
      </div>

      <CompanyDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingRecord(null);
        }}
        initialValue={editingRecord || {}}
        onSubmit={handleSubmit}
        onDelete={(rec) => {
          setPendingDelete(rec);
          setShowConfirmDialog(true);
        }}
        loading={loading}
      />

      <ConfirmationModal
        title="Delete company"
        open={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setPendingDelete(null);
        }}
        onConfirm={handleDelete}
      >
        Are you sure you want to permanently delete{" "}
        <b>{pendingDelete?.name || "this company"}</b>? This action cannot be undone.
      </ConfirmationModal>
    </Box>
  );
}
